import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import SMQMainContainer from "../../../components/SMQMainContainer";
import useSmqPermissions from "../../../hooks/useSmqPermissions";
import ProcessusService from "../../../services/smq/ProcessusService";
import { fieldSx, OUTIL_COLORS, pageSx, paperSx } from "./OutilContainer.styles";
import OutilEntryPanel from "./OutilEntryPanel";
import TOOL_DEFINITIONS from "./toolDefinitions";

const sameId = (left, right) =>
  left !== null &&
  left !== undefined &&
  right !== null &&
  right !== undefined &&
  String(left) === String(right);

const processLabel = (processus) => {
  const instance = String(processus?.instance || "").trim();
  const name = String(processus?.referentiel_nom || "").trim();
  const direction = String(processus?.direction_code || "").trim();
  return `${instance}${name ? ` — ${name}` : ""}${direction ? ` (${direction})` : ""}`;
};

const OutilContainer = () => {
  const { canTreat } = useSmqPermissions();
  const { processus_id: processusId } = useParams();
  const navigate = useNavigate();
  const [processus, setProcessus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeToolKey, setActiveToolKey] = useState(TOOL_DEFINITIONS[0].key);
  const [outilEntry, setOutilEntry] = useState(null);
  const [toolDrafts, setToolDrafts] = useState({});
  const [toolDirty, setToolDirty] = useState(false);
  const [entryRefreshKey, setEntryRefreshKey] = useState(0);
  const requestIdRef = useRef(0);

  const loadProcessus = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError(null);

    try {
      const payload = await ProcessusService.getProcessus();
      if (!Array.isArray(payload?.data)) {
        throw new Error("Le service des processus a retourné une réponse invalide.");
      }
      if (requestId === requestIdRef.current) {
        setProcessus(payload.data);
      }
    } catch {
      if (requestId === requestIdRef.current) {
        setProcessus([]);
        setError("Une erreur s’est produite lors de la récupération des données.");
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadProcessus();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadProcessus]);

  const selectedProcessus = useMemo(
    () => processus.find((row) => sameId(row.processus_id, processusId)) || null,
    [processus, processusId]
  );

  const directions = useMemo(() => {
    const byId = new Map();
    processus.forEach((row) => {
      if (row.direction_id === null || row.direction_id === undefined) return;
      const key = String(row.direction_id);
      if (!byId.has(key)) {
        byId.set(key, {
          id: row.direction_id,
          label: [row.direction_code, row.direction_name].filter(Boolean).join(" — ") || `Direction ${key}`,
        });
      }
    });
    return [...byId.values()].sort((left, right) => left.label.localeCompare(right.label, "fr", { sensitivity: "base" }));
  }, [processus]);

  const availableProcessus = useMemo(
    () =>
      selectedProcessus
        ? processus.filter((row) => sameId(row.direction_id, selectedProcessus.direction_id))
        : [],
    [processus, selectedProcessus]
  );

  const activeTool = useMemo(
    () => TOOL_DEFINITIONS.find((tool) => tool.key === activeToolKey) || TOOL_DEFINITIONS[0],
    [activeToolKey]
  );

  const draftKey = selectedProcessus ? `${selectedProcessus.processus_id}:${activeTool.key}` : "";
  const toolValue = toolDrafts[draftKey] || {};
  const ActiveToolComponent = activeTool.component;

  const navigateToProcessus = (id) => {
    if (toolDirty && !window.confirm("Les modifications non enregistr?es seront perdues. Continuer ?")) return;
    setToolDirty(false);
    navigate(`/smq_nc/processus/outils/${id}`);
  };

  const handleDirectionChange = (event) => {
    const firstProcessus = processus.find((row) => sameId(row.direction_id, event.target.value));
    if (firstProcessus) navigateToProcessus(firstProcessus.processus_id);
  };

  const handleToolChange = (_, value) => {
    if (toolDirty && !window.confirm("Les modifications non enregistr?es seront perdues. Continuer ?")) {
      return;
    }
    setToolDirty(false);
    setActiveToolKey(value);
  };

  const updateToolValue = (field, value) => {
    setToolDrafts((current) => ({
      ...current,
      [draftKey]: { ...(current[draftKey] || {}), [field]: value },
    }));
  };

  return (
    <SMQMainContainer>
      <Box sx={pageSx}>
        {loading ? (
          <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", minHeight: 320 }}>
            <CircularProgress aria-label="Chargement des outils du processus" size={32} />
          </Box>
        ) : error ? (
          <Alert action={<Button color="inherit" onClick={loadProcessus}>Réessayer</Button>} severity="error">
            {error}
          </Alert>
        ) : !selectedProcessus ? (
          <Alert
            action={<Button color="inherit" onClick={() => navigate("/smq_nc/parametrage-processus/processus")}>Retour aux processus</Button>}
            severity="warning"
          >
            Le processus demandé est introuvable ou n’est plus actif.
          </Alert>
        ) : (
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Box sx={{ alignItems: { md: "flex-start" }, display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, justifyContent: "space-between" }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography component="h1" sx={{ alignItems: "center", color: OUTIL_COLORS.text, display: "flex", fontFamily: "Georgia, serif", fontSize: { xs: 18, sm: 21 }, fontWeight: 700, gap: 0.8, lineHeight: 1.25 }}>
                  <BuildOutlinedIcon sx={{ color: "#8066a8", fontSize: 19 }} />
                  Outils — {selectedProcessus.instance} — {selectedProcessus.referentiel_nom || "Processus"}
                </Typography>
                <Typography color={OUTIL_COLORS.textMuted} fontSize={11} sx={{ mt: 0.4 }}>
                  {[selectedProcessus.categorie_name, `Direction ${selectedProcessus.direction_code || "—"}`, `Pilote ${selectedProcessus.pilote_name?.trim() || "non défini"}`].join(" · ")}
                </Typography>
              </Box>
              <Box sx={{ alignItems: { sm: "center" }, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1, width: { xs: "100%", md: "auto" } }}>
                <TextField
                  aria-label="Sélectionner une instance de processus"
                  onChange={(event) => navigateToProcessus(event.target.value)}
                  select
                  size="small"
                  sx={{ ...fieldSx, minWidth: { xs: "100%", sm: 280 } }}
                  value={selectedProcessus.processus_id}
                >
                  {availableProcessus.map((row) => <MenuItem key={row.processus_id} value={row.processus_id}>{processLabel(row)}</MenuItem>)}
                </TextField>
                <Tooltip title="Export disponible dans une prochaine version">
                  <span>
                    <Button disabled startIcon={<FileDownloadOutlinedIcon />} sx={{ color: OUTIL_COLORS.navy, fontSize: 11, fontWeight: 700, textTransform: "none", whiteSpace: "nowrap", "&.Mui-disabled": { color: OUTIL_COLORS.navy, opacity: 0.65 } }}>
                      Exporter outil (.csv)
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            <Paper elevation={0} sx={{ ...paperSx, backgroundColor: "#e3eaf2", p: 1.5 }}>
              <Box sx={{ alignItems: { sm: "center" }, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.25 }}>
                <Typography color={OUTIL_COLORS.navy} fontSize={10} fontWeight={700} letterSpacing="0.05em">DIRECTION :</Typography>
                <TextField
                  aria-label="Sélectionner une direction"
                  onChange={handleDirectionChange}
                  select
                  size="small"
                  sx={{ ...fieldSx, minWidth: { xs: "100%", sm: 280 } }}
                  value={selectedProcessus.direction_id}
                >
                  {directions.map((direction) => <MenuItem key={direction.id} value={direction.id}>{direction.label}</MenuItem>)}
                </TextField>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ borderBottom: `4px solid ${OUTIL_COLORS.border}`, borderRadius: "6px 6px 0 0", overflow: "hidden" }}>
              <Tabs
                aria-label="Outils du processus"
                onChange={handleToolChange}
                scrollButtons="auto"
                sx={{ minHeight: 40, "& .MuiTab-root": { border: `1px solid ${OUTIL_COLORS.border}`, borderRadius: "6px", fontSize: 10.5, fontWeight: 700, minHeight: 34, minWidth: "max-content", mx: 0.3, my: 0.4, px: 1.2, textTransform: "none" }, "& .Mui-selected": { backgroundColor: `${OUTIL_COLORS.navy} !important`, color: "#fff !important" }, "& .MuiTabs-indicator": { display: "none" } }}
                value={activeTool.key}
                variant="scrollable"
              >
                {TOOL_DEFINITIONS.map((tool) => (
                  <Tab aria-current={activeTool.key === tool.key ? "page" : undefined} key={tool.key} label={`${tool.number}. ${tool.label}`} value={tool.key} />
                ))}
              </Tabs>
            </Paper>

            {!canTreat && (
              <Alert severity="info">
                Consultation seule : le droit Traitement est nécessaire pour modifier les outils.
              </Alert>
            )}
            <Box
              component="fieldset"
              disabled={!canTreat}
              sx={{ border: 0, m: 0, minWidth: 0, p: 0 }}
            >
              <OutilEntryPanel
                key={`${selectedProcessus.processus_id}:${entryRefreshKey}`}
                onEntryChange={setOutilEntry}
                processus={selectedProcessus}
              />
            </Box>
            <Box
              component="fieldset"
              disabled={!canTreat && activeTool.key !== "fiche"}
              sx={{ border: 0, m: 0, minWidth: 0, p: 0 }}
            >
              <ActiveToolComponent
                onChange={updateToolValue}
                onDirtyChange={setToolDirty}
                onDocumentEntryRefresh={(entryId) => {
                  setOutilEntry((current) => ({
                    ...(current || {}),
                    outil_entree_id: entryId,
                  }));
                  setEntryRefreshKey((current) => current + 1);
                }}
                outilEntree={outilEntry}
                processus={selectedProcessus}
                processusOptions={processus}
                value={toolValue}
              />
            </Box>
          </Box>
        )}
      </Box>
    </SMQMainContainer>
  );
};

export default OutilContainer;
